import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { get } from "lodash";

import { queryClient } from "App";
import { formatError } from "helpers/formatError";
import { VOICEMAILS_TABLE_CONSTANTS } from "routes/voicemail/components/voicemail_table/VoicemailTableV3";
import UserService from "services/User";
import VoicemailService from "services/Voicemails";
import { addResizedColumns } from "hooks/useTableConfig";
import { getVoicemailTableColumns } from "routes/voicemail/components/voicemail_table/VoicemailColumnsAndFilters";

export const invalidateVoicemailsQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [VOICEMAILS_TABLE_CONSTANTS.queryKey],
  });
};

export const useVoicemailMutations = ({ onDeleted } = {}) => {
  const create = useMutation({
    mutationFn: data => VoicemailService.createVoicemail(data),
    onSuccess: () => invalidateVoicemailsQuery(),
    onError: err => toast.error(formatError(err, "Failed to save voicemail")),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) => VoicemailService.editVoicemail(id, data),
    onSuccess: () => invalidateVoicemailsQuery(),
    onError: err => toast.error(formatError(err, "Failed to save voicemail")),
  });

  const remove = useMutation({
    mutationFn: id => VoicemailService.deleteVoicemail(id),
    onSuccess: () => {
      invalidateVoicemailsQuery();
      toast.success("Voicemail deleted");
      onDeleted?.();
    },
    onError: err => toast.error(formatError(err, "Failed to delete voicemail")),
  });

  return { create, update, remove };
};

export const useVoicemailTable = () => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getVoicemailTableColumns();
    const voicemailsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${VOICEMAILS_TABLE_CONSTANTS.tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: voicemailsTableSettings?.resized_columns || {},
    });
    return withResizedColumns;
  }, [tableSettings, isTableSettingsFetched]);

  return {
    columns,
  };
};
